<template>
  <div>
    <div>
      <BaseButton
        :text="`Back to 'Your quotes'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToYourQuotesStep"
      />

      <div class="mt-10 d-flex justify-space-between">
        <h2 class="font-24 font-lg-28 font-weight-bold primary--text">
          {{ $vuetify.breakpoint.lgAndUp ? "Your application" : "Application" }}
        </h2>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 mt-1"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <ValidationObserver ref="observerFormApplication" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <PolicyDetail class="mb-9"></PolicyDetail>
        <MortgageDetail class="mb-9"></MortgageDetail>
        <ApplicantDetail
          :applicantTitle="'Applicant 1 details'"
          :questions="questionApplicant1"
          class="mb-3"
        ></ApplicantDetail>
        <ApplicantDetail
          v-if="hasApplicant2"
          :applicantTitle="'Applicant 2 details'"
          :questions="questionApplicant2"
          :occupationHelpText="LABEL_CONSTANTS.SECOND_OCCUPATION_HINT_HELP_TEXT"
        ></ApplicantDetail>
        <BaseLabel text="Your reference (optional)" class="mt-3 mb-2" />
        <BaseInput
          v-model="questionYourRef.Value"
          :vid="questionYourRef.InterfaceKey"
          :data-uiid="questionYourRef.InterfaceKey"
          :hide-details="false"
          :rules="yourRefRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />
        <BaseLabel
          text="Additional information (optional)"
          labelClass="mb-3 font-18 font-weight-medium"
        />
        <BaseTextarea
          vid="AdditionalInfo"
          class="font-18 font-weight-medium"
          hideValidationIcon
          v-model="questionAdditionalInfo.Value"
          no-resize
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :rules="{
            max: {
              length: 200,
              fieldLabel: 'Additional information (optional)',
              errorMessageTemplate:
                '{0} may not be greater than {1} characters.',
            },
          }"
        />
        <BaseButton
          rounded
          class="mt-3 font-weight-bold continue-button primary--text"
          height="60"
          type="submit"
          :text="
            $vuetify.breakpoint.lgAndUp ? 'CONTINUE TO PAYMENT' : 'CONTINUE'
          "
          :color="'psorange'"
          :fontSize="'font-20'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '390' : '100%'"
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { EventBus } from "@/common/eventbus.js";
import { scrollTop, tryGoToNextStep } from "@/common/utils";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState } from "vuex";
import { ROUTE_NAME } from "@/constants/route.constants";
import {
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
} from "@/store/types/module-types";

import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import PolicyDetail from "./YourApplicationComponent/PolicyDetail.vue";
import MortgageDetail from "./YourApplicationComponent/MortgageDetail.vue";
import ApplicantDetail from "./YourApplicationComponent/ApplicantDetail.vue";
import * as ERROR_MESSAGES from "@/constants/message.constants.js";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX } from "@/constants/regex-patterns.constants";
import { addFieldLabel } from "@/common/utils";
export default {
  name: "MPPIYourApplicationPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseTextarea,
    BaseInput,
    BaseLabel,
    PolicyDetail,
    MortgageDetail,
    ApplicantDetail,
  },
  data() {
    return {
      questionYourRef: {},
      questionAdditionalInfo: {},
      USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
      ERROR_MESSAGES,
      LABEL_CONSTANTS,
      yourRefRules: addFieldLabel(
        {
          max: {
            length: 40,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
            customMessage: ERROR_MESSAGES.ERROR_MSG_USER_REF,
          },
        },
        "Your reference"
      ),
      questionApplicant1: {
        telNo: {},
        jobRole: {},
        emailAddress: {},
        employmentStatus: {},
        employerName: {},
        employerLengthOfService: {},
      },
      questionApplicant2: {
        telNo: {},
        jobRole: {},
        emailAddress: {},
        employmentStatus: {},
        employerName: {},
        employerLengthOfService: {},
      },
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "restoringLastStep",
      "questionMap",
      "userReference",
    ]),
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
    }),
    hasApplicant2() {
      return (
        this.questionMap["Applicant2DoB"].Value ||
        this.questionMap["Applicant2Forename"].Value ||
        this.questionMap["Applicant2Surname"].Value ||
        this.questionMap["Applicant2Title"].Value
      );
    },
  },
  beforeMount() {
    this.onSetupAndLoadSavedData();
    this.questionYourRef = this.applicationQuestionMap["YourRef"];
    if (this.userReference) {
      this.questionYourRef.Value = this.userReference;
    }
    this.questionAdditionalInfo = this.applicationQuestionMap["AdditionalInfo"];
    scrollTop();
  },
  mounted() {
    this.setCurrentRefObserverForm(this.$refs.observerFormApplication);
    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.observerFormApplication,
        async (form) => await form.validate(),
        ROUTE_NAME.MPPI.B2B.PAYMENT
      );
    }, 0);
  },
  watch: {
    "questionYourRef.Value"(value) {
      this.setUserReference(value);
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "refreshSessionAndNextStep",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "setUserReference",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.refreshSessionAndNextStep(ROUTE_NAME.MPPI.B2B.PAYMENT);
    },
    initializeApplicantData(applicantKeyPrefix) {
      return {
        telNo: this.applicationQuestionMap[`${applicantKeyPrefix}TelNo`],
        jobRole: this.applicationQuestionMap[`${applicantKeyPrefix}JobRole`],
        emailAddress:
          this.applicationQuestionMap[`${applicantKeyPrefix}EmailAddress`],
        employmentStatus:
          this.applicationQuestionMap[`${applicantKeyPrefix}EmploymentStatus`],
        employerName:
          this.applicationQuestionMap[`${applicantKeyPrefix}EmployerName`],
        employerLengthOfService:
          this.applicationQuestionMap[
            `${applicantKeyPrefix}EmployerLengthOfService`
          ],
      };
    },

    onSetupAndLoadSavedData() {
      this.questionApplicant1 = this.initializeApplicantData("Applicant1");
      this.questionApplicant2 = this.initializeApplicantData("Applicant2");
    },
    backToYourQuotesStep() {
      this.$router.push({
        name: ROUTE_NAME.MPPI.B2B.QUOTATION_RESULT,
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
